import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { ProfileContext } from "../../../context/ProfileProvider";
import FuturesTransferModal from "./FuturesTransferModal";

const FuturesTradeTransferContext = createContext(null);

export function useFuturesTradeTransfer() {
  return useContext(FuturesTradeTransferContext);
}

export function FuturesTradeTransferProvider({ children, isMargin }) {
  const profile = useContext(ProfileContext);
  const refreshEstimatedPortfolio = profile?.refreshEstimatedPortfolio;

  const [fundData, setFundData] = useState([]);
  const [currencyData, setCurrencyData] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState({});
  const [availableWalletTypes, setAvailableWalletTypes] = useState([]);
  const [fromWalletType, setFromWalletType] = useState("spot");
  const [toWalletType, setToWalletType] = useState("futures");
  const [transferable, setTransferable] = useState("0");
  const [balanceLoading, setBalanceLoading] = useState(false);
  const balanceRequestRef = useRef(0);
  const [transferAmount, setTransferAmount] = useState("");
  const [transferCoinSearch, setTransferCoinSearch] = useState("");
  const [isTransferOpen, setIsTransferOpen] = useState(false);

  const showWalletTransferModalImperative = useCallback(() => {
    setIsTransferOpen(true);
  }, []);

  const handleUserFunds = useCallback(async (type) => {
    try {
      const result = await AuthService.getUserfunds(type);
      if (result?.success) {
        const wallets = result?.data || [];
        setFundData(wallets);
        setCurrencyData((prev) => (prev && prev.length ? prev : wallets));
        return wallets;
      }
    } catch {
      /* silent */
    }
    return null;
  }, []);

  const loadWalletTypes = useCallback(async () => {
    try {
      const result = await AuthService.availableWalletTypes();
      if (result?.success) {
        const types = Array.isArray(result?.data)
          ? result.data.map((w) => (typeof w === "string" ? w : w?.type)).filter(Boolean)
          : [];
        setAvailableWalletTypes(types);
        const from = types.includes("spot") ? "spot" : types[0] || "spot";
        const to = types.includes("futures") ? "futures" : types.find((t) => t !== from) || "main";
        setFromWalletType(from);
        setToWalletType(to);
      }
    } catch {
      /* silent */
    }
  }, []);

  const refreshTransferBalance = useCallback(async () => {
    if (!selectedCurrency?.currency_id || !fromWalletType || !toWalletType) {
      setBalanceLoading(false);
      return;
    }

    const requestId = ++balanceRequestRef.current;
    setBalanceLoading(true);
    try {
      const result = await AuthService.getTransferBalance({
        fromWallet: fromWalletType,
        toWallet: toWalletType,
        currencyId: selectedCurrency.currency_id,
      });
      if (requestId !== balanceRequestRef.current) return;
      if (result?.success) {
        setTransferable(result.data?.transferable ?? "0");
      } else {
        setTransferable("0");
      }
    } catch {
      if (requestId !== balanceRequestRef.current) return;
      setTransferable("0");
    } finally {
      if (requestId === balanceRequestRef.current) {
        setBalanceLoading(false);
      }
    }
  }, [selectedCurrency?.currency_id, fromWalletType, toWalletType]);

  const handleInterchangeWallet = useCallback(() => {
    setFromWalletType(toWalletType);
    setToWalletType(fromWalletType);
  }, [fromWalletType, toWalletType]);

  const fiveDecimal = useCallback((data) => {
    if (typeof data === "number") {
      if (data <= 0) return "0.00";
      return parseFloat(data.toFixed(8));
    }
    return "N/A";
  }, []);

  const handleWalletTransfer = useCallback(async () => {
    if (!fromWalletType || !toWalletType || !selectedCurrency?.currency_id || !+transferAmount) {
      alertErrorMessage("Please fill all required fields");
      return;
    }
    const max = parseFloat(transferable || 0);
    if (Number(transferAmount) > max) {
      alertErrorMessage("Amount exceeds available balance");
      return;
    }

    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.walletTransferUnified({
        fromWallet: fromWalletType,
        toWallet: toWalletType,
        currencyId: selectedCurrency.currency_id,
        amount: transferAmount,
      });

      if (result?.success) {
        alertSuccessMessage(result?.message || "Transfer successful");
        setTransferAmount("");
        const wallets = await handleUserFunds("");
        await refreshEstimatedPortfolio?.("");
        const cid = selectedCurrency?.currency_id;
        if (cid && Array.isArray(wallets)) {
          const next = wallets.find((w) => String(w?.currency_id) === String(cid));
          if (next) setSelectedCurrency(next);
        }
        await refreshTransferBalance();
        setIsTransferOpen(false);
      } else {
        alertErrorMessage(result?.error?.message || result?.message || "Transfer failed");
      }
    } catch (e) {
      alertErrorMessage(e?.message || "Transfer failed");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, [
    fromWalletType,
    toWalletType,
    selectedCurrency,
    transferAmount,
    transferable,
    refreshTransferBalance,
    handleUserFunds,
    refreshEstimatedPortfolio,
  ]);

  useEffect(() => {
    void (async () => {
      await loadWalletTypes();
      await handleUserFunds("");
    })();
  }, [loadWalletTypes, handleUserFunds]);

  useEffect(() => {
    void refreshTransferBalance();
  }, [refreshTransferBalance]);

  const openWalletTransfer = useCallback(() => {
    const usdt = fundData.find((r) => String(r?.short_name || "").toUpperCase() === "USDT");
    setSelectedCurrency(usdt || fundData[0] || {});
    if (isMargin) {
      setFromWalletType("isolated");
      setToWalletType("spot");
    } else {
      setFromWalletType("spot");
      setToWalletType(availableWalletTypes.includes("futures") ? "futures" : availableWalletTypes.find((t) => t !== "spot") || "main");
    }
    setIsTransferOpen(true);
  }, [fundData, availableWalletTypes, isMargin]);

  const value = { openWalletTransfer };

  return (
    <FuturesTradeTransferContext.Provider value={value}>
      {children}
      <FuturesTransferModal
        isMargin={isMargin}
        open={isTransferOpen}
        onClose={() => setIsTransferOpen(false)}
        fromWalletType={fromWalletType}
        setFromWalletType={setFromWalletType}
        toWalletType={toWalletType}
        setToWalletType={setToWalletType}
        availableWalletTypes={availableWalletTypes}
        selectedCurrency={selectedCurrency}
        setSelectedCurrency={setSelectedCurrency}
        transferAmount={transferAmount}
        setTransferAmount={setTransferAmount}
        transferable={transferable}
        balanceLoading={balanceLoading}
        handleInterchangeWallet={handleInterchangeWallet}
        handleWalletTransfer={handleWalletTransfer}
        currencyData={currencyData}
        transferCoinSearch={transferCoinSearch}
        setTransferCoinSearch={setTransferCoinSearch}
        fiveDecimal={fiveDecimal}
      />
    </FuturesTradeTransferContext.Provider>
  );
}
